import { useCallback, useRef } from "react";
import { type ReportType } from "~/features/cart/cart/components/Buttons/DownloadReportButton/api/downloadCartReport";
import { DownloadReportButtons } from "~/features/orders/common/components/DownloadReportButtons";
import Spinner from "~/common/components/Spinner/Spinner";
import { toMapFn } from "~/common/utils/reducers";
import { QuoteListTable } from "~/features/orders/quoteList/components/QuoteListTable/QuoteListTable";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import {
  downloadQuoteHistoryReport,
  getReportLabels,
} from "~/features/orders/quoteList/api/downloadQuoteHistoryReport";
import { useSearchSubscription } from "~/features/orders/common/hooks/useSearchSubscription";
import { getIsAllOpen, rowHasSurcharge } from "~/features/orders/orderList/OrderListPage";
import {
  OrderListSearchBox,
  type OrderListSearchBoxData,
  type OrderListSearchBoxHandle,
} from "~/features/orders/orderList/components/OrderListSearchBox/OrderListSearchBox";
import {
  filterProductsMO,
  type GetOrderListSearchResults,
  getQuoteListSearchResults,
  transformFormData,
} from "~/features/orders/common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import { SearchBoxVariant } from "~/features/orders/orderList/components/OrderListSearchBox/SearchBoxVariant";
import { fetchQuoteDetails } from "~/features/orders/quoteList/api/fetchOrderDetails/fetchQuoteDetails";
import { useQuoteIsBundleInfoModal } from "~/features/orders/quoteList/components/QuoteIsBundleInfoModal";
import { useTranslation } from "react-i18next";
import { transformQuoteDetails } from "~/features/orders/quoteList/api/fetchOrderDetails/v2/transformQuoteDetails";
import { MultiAccountSearchDataProvider } from "~/features/orders/orderList/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSearchDataProvider";
import {
  canStartLoadingDetails,
  quoteListPageActions,
  type QuoteListRow,
  useQuoteListState,
} from "~/features/orders/quoteList/useQuoteListState";
import { Stack, styled } from "~/common/mui";
import { useQuoteListSelectionState } from "~/features/orders/common/components/v2/QuoteTable/useQuoteListSelectionState";
import { canQuoteListRowBeSelected } from "~/features/orders/quoteList/utils";
import { useDispatch } from "react-redux";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { isErrorEntry, isFromDifferentAccOrSalesOrg } from "~/features/orders/common/utils/util";
import { Alert } from "~/common/mui/Alert/Alert";
import { ATCBar } from "~/features/orders/common/components/ATCBar";
import { useQuoteAddToCart } from "~/features/orders/quoteList/useQuoteAddToCart";
import { PageElements } from "~/common/components/PageElements";
import { OrderQuoteNoResults } from "~/common/components/NoResultsSection/OrderQuoteNoResults";
import {
  OrderListDesignToggle,
  OrderListDesignToggleProvider,
  useDesignToggle,
} from "../common/components/NewDesignToggle";
import { QuoteListTableV2 } from "../quoteListV2/QuoteListTableV2";
import { orderHistoryStyles } from "~/features/orders/orderStyles";

const QuoteHistoryStyleScope = styled(Stack)(({ theme }) => orderHistoryStyles(theme));

export const QuoteListPage = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useQuoteListState();
  const reduxDispatch = useDispatch();
  const selectionContext = useQuoteListSelectionState();
  const { loading, error, results: rows } = state;
  const submittedFormData = useRef<GetOrderListSearchResults | null>(null);
  const searchBoxRef = useRef<OrderListSearchBoxHandle>(null);
  const { isNewDesignEnabled } = useDesignToggle();

  const isAllOpen = getIsAllOpen(state.results);
  const webservicesDisabled = window.SCHA.config.disableAllWS;

  const { addQuotesToCart, isATCInProgress, selectedATCItems, disableCheckoutBtn } = useQuoteAddToCart({
    rows,
    selectionPerDocument: selectionContext.selectionPerDocument,
  });

  const { requestConfirmation } = useQuoteIsBundleInfoModal();

  const search = useSearchSubscription(getQuoteListSearchResults, {
    onBegin: () => dispatch(quoteListPageActions.beginLoading()),
    onError: () => dispatch(quoteListPageActions.errorLoading()),
    onNext: (data) => dispatch(quoteListPageActions.addResultPage(data)),
    onComplete: () => dispatch(quoteListPageActions.finishLoading()),
  });

  const onSubmitHandler = useCallback(
    (formData: OrderListSearchBoxData) => {
      const transformedData = transformFormData(formData);
      submittedFormData.current = transformedData;
      search(transformedData);
    },
    [search],
  );

  const loadSingleDetail = useCallback(async (row: QuoteListRow, preselect: boolean) => {
    const soNumber = row.data.soNumber;
    let accountNumber, salesOrg;
    if (isFromDifferentAccOrSalesOrg(row)) {
      accountNumber = row.data.account?.code;
      salesOrg = row.data.account?.salesOrg;
    }
    try {
      const dto = await fetchQuoteDetails(soNumber, accountNumber, salesOrg);
      const data = transformQuoteDetails(dto);
      reduxDispatch(addBaseProductDataAction(data.quoteLines.map((line) => line.product)));
      data.quoteLines = filterProductsMO(data.quoteLines, submittedFormData.current);
      selectionContext.init(data, preselect);
      dispatch(quoteListPageActions.finishLoadingDetails(data));
    } catch {
      dispatch(quoteListPageActions.errorLoadingDetails(soNumber));
    }
  }, []);

  const toggleOpenCb = useCallback(
    (row: QuoteListRow) => {
      if (canStartLoadingDetails(row)) {
        loadSingleDetail(row, false);
      }
      dispatch(quoteListPageActions.toggleOpen(row.data.soNumber));
    },
    [loadSingleDetail],
  );

  const toggleOpenAllCb = useCallback(() => {
    rows.forEach((row) => {
      const newState = !isAllOpen;
      if (newState && canStartLoadingDetails(row) && !isErrorEntry(row)) {
        // TODO - fetch service?
        loadSingleDetail(row, false);
      }
    });
    dispatch(quoteListPageActions.toggleAllOpen());
  }, [isAllOpen, loadSingleDetail, rows]);

  const toggleSelectAllQuotes = useCallback(() => {
    rows.forEach((row) => {
      if (!canQuoteListRowBeSelected(row)) {
        return;
      }
      if (canStartLoadingDetails(row)) {
        loadSingleDetail(row, true);
        if (!row.open) {
          dispatch(quoteListPageActions.toggleOpen(row.data.soNumber));
        }
      } else {
        selectionContext.toggleAllLines(row.data.soNumber);
      }
    });
  }, [rows, loadSingleDetail, selectionContext.toggleAllLines]);

  const toggleSelectLineItem = useCallback(
    async (soNumber: string, quoteLineNo: string) => {
      const quote = rows.find((item) => item.data.soNumber === soNumber)!;
      if (quote.data.isBundled) {
        const confirmation = await requestConfirmation();
        if (confirmation) {
          selectionContext.toggleAllLines(soNumber);
        }
      } else {
        selectionContext.toggleLine(soNumber, quoteLineNo);
      }
    },
    [rows, selectionContext.toggleAllLines, selectionContext.toggleLine],
  );

  const toggleSelectQuote = useCallback(
    (row: QuoteListRow) => {
      if (canStartLoadingDetails(row)) {
        loadSingleDetail(row, true);
        if (!row.open) {
          dispatch(quoteListPageActions.toggleOpen(row.data.soNumber));
        }
        return;
      } else {
        selectionContext.toggleAllLines(row.data.soNumber);
      }
    },
    [loadSingleDetail, selectionContext.toggleAllLines],
  );

  const downloadQuoteList = (fileType: ReportType) => {
    const reportLabels = toMapFn(
      getReportLabels(),
      (key) => key,
      (key) => t(key),
    );
    downloadQuoteHistoryReport(rows, fileType, reportLabels);
  };

  const containsSurcharge = rows.some(rowHasSurcharge);

  const disableInteractions = loading || error || webservicesDisabled;
  const showRows = !error && rows.length > 0;

  return (
    <QuoteHistoryStyleScope gap={3}>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <PageElements.Title>
          {t("quoteListPage.title")} (<span data-test-id={"quotesAmount"}>{rows.length}</span>)
        </PageElements.Title>

        {isNewDesignEnabled && !loading && rows.length > 0 && (
          <DownloadReportButtons
            onDownload={downloadQuoteList}
            disabled={disableInteractions}
            testIdPrefix="quoteList-searchBox"
            label={t("quoteListPage.download.pdf")}
          />
        )}
      </Stack>

      <OrderListSearchBox ref={searchBoxRef} onSubmit={onSubmitHandler} variant={SearchBoxVariant.QUOTE}>
        {!isNewDesignEnabled && !loading && rows.length > 0 && (
          <DownloadReportButtons
            onDownload={downloadQuoteList}
            disabled={disableInteractions}
            testIdPrefix="quoteList-searchBox"
            label={t("quoteListPage.download.pdf")}
          />
        )}
      </OrderListSearchBox>

      <OrderListDesignToggle />

      {loading && <Spinner />}

      {showRows &&
        (isNewDesignEnabled ? (
          <QuoteListTableV2
            rows={rows}
            toggleOpen={toggleOpenCb}
            toggleOpenAll={toggleOpenAllCb}
            toggleSelectLineItem={toggleSelectLineItem}
            toggleSelectQuote={toggleSelectQuote}
            toggleSelectAllQuotes={toggleSelectAllQuotes}
            selectionPerDocument={selectionContext.selectionPerDocument}
          />
        ) : (
          <QuoteListTable
            rows={rows}
            toggleOpen={toggleOpenCb}
            toggleOpenAll={toggleOpenAllCb}
            toggleSelectLineItem={toggleSelectLineItem}
            toggleSelectQuote={toggleSelectQuote}
            toggleSelectAllQuotes={toggleSelectAllQuotes}
            selectionPerDocument={selectionContext.selectionPerDocument}
          />
        ))}

      {<IncludesSurchargeDisclaimer hasSurcharge={containsSurcharge} />}

      {!webservicesDisabled && !loading && error && <Alert severity="error">{t("quoteListPage.error")}</Alert>}

      {!loading && !error && !rows.length && (
        <OrderQuoteNoResults
          title={t("page.quoteList.noResults.title")}
          onReset={() => searchBoxRef.current?.reset()}
        />
      )}

      {selectedATCItems.selectedLineItems > 0 && (
        <ATCBar
          selectedLineItems={selectedATCItems.selectedLineItems}
          selectedQuantity={selectedATCItems.selectedQuantity}
          totalPrice={selectedATCItems.totalPrice}
          onAddToCart={addQuotesToCart}
          loading={isATCInProgress}
        />
      )}
    </QuoteHistoryStyleScope>
  );
};

export function createQuoteListPage(data: { accounts: string; filters: string }) {
  return (
    <MultiAccountSearchDataProvider accountsJson={data.accounts}>
      <OrderListDesignToggleProvider>
        <QuoteListPage />
      </OrderListDesignToggleProvider>
    </MultiAccountSearchDataProvider>
  );
}
